import React, { useEffect, useMemo, useRef } from 'react'
import { useRaportForm } from '../../context/RaportFormContext'
import Options from './stages/Options'
import StageHeader from '@/modules/drivers/forms/shared/StageHeader'
import { faSlidersSolid } from '@/lib/fa'
import { DividerHorizontal } from '@/modules/drivers/shared/DividerHorizontal'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { useQueryCountries } from '@/lib/api/company/country'
import { Dropdown } from '@/ui/form/controls/Dropdown'

const FOREIGN_REPORT_COUNTRY_CODES = new Set([
  'AT',
  'BE',
  'BG',
  'HR',
  'CZ',
  'DK',
  'EE',
  'FI',
  'FR',
  'ES',
  'CY',
  'GR',
  'NL',
  'IE',
  'LT',
  'LV',
  'LU',
  'DE',
  'PT',
  'RO',
  'SK',
  'SE',
  'CH',
  'IT',
  'HU',
  'GB',
  'SI',
  'MT',
])

const EuRate = ({ companyId, includeNorway = false }) => {
  const { data: countries, isLoading } = useQueryCountries({ companyId })
  const { data, setData } = useRaportForm()
  const { t } = useLocalize()
  const hasResetData = useRef(false)
  const initializedCompanyId = useRef<string | null>(null)

  useEffect(() => {
    if (hasResetData.current) return

    hasResetData.current = true
    setData({
      timesheetOption: 0,
      printDayNotes: false,
      printDayActivities: false,
      separateFiles: false,
      issuerSignature: false,
      driverSignature: false,
      countries: [],
    })
  }, [setData])

  const availableCountries = useMemo(
    () =>
      (countries || []).filter((country) =>
        FOREIGN_REPORT_COUNTRY_CODES.has(country.code) ||
        (includeNorway && country.code === 'NO'),
      ),
    [countries, includeNorway],
  )

  useEffect(() => {
    if (
      availableCountries.length === 0 ||
      initializedCompanyId.current === companyId
    ) {
      return
    }

    initializedCompanyId.current = companyId
    setData((currentData) => ({
      ...currentData,
      countries: availableCountries,
    }))
  }, [availableCountries, companyId, setData])

  const countryOptions = useMemo(
    () =>
      availableCountries.map((country) => ({
        value: country.id.toString(),
        label: country.name,
        code: country.code,
      })),
    [availableCountries],
  )

  const selectedCountryIds = useMemo(
    () => (data?.countries || []).map((c) => c.id.toString()),
    [data?.countries],
  )

  return (
    <div className="tw-flex-1 tw-space-y-4 tw-overflow-auto tw-p-4 tw-pr-8">
      <div className="tw-flex tw-items-start">
        <StageHeader title={t('printout.data')} textSize="sm" icon={faSlidersSolid} />
        <div className="tw-flex tw-flex-1 tw-flex-col tw-gap-1 sm:tw-min-w-[400px]">
          <label className="tw-group tw-mb-0 tw-px-1 tw-pb-0.5 tw-text-sm tw-font-medium">
            {t('printout.countries')}
          </label>
          {/* Portal: cialo modala ma overflow, lista byla przycinana do jego wysokosci. */}
          <Dropdown
            multiple
            showSelectAll
            searchable
            portalMenu
            placeholder={t('printout.select-countries')}
            value={selectedCountryIds}
            name="countries"
            onChange={(values: string[]) => {
              const selectedCountries = availableCountries.filter((c) =>
                values.includes(c.id.toString()),
              )
              setData((currentData) => ({
                ...currentData,
                countries: selectedCountries,
              }))
            }}
            options={countryOptions}
            disabled={isLoading}
          />
        </div>
      </div>
      <DividerHorizontal />
      <Options
        setData={setData}
        data={data}
        vehicleActivity={false}
        separateFilesOnly={false}
      />
    </div>
  )
}

export default EuRate
